import type { NavigationGuard } from 'vue-router'
import { routeNames } from '@/router/names.ts'
import { useAccountStore } from '@/stores/account.ts'
import { useSubAccountStore } from '@/stores/subAccount.ts'
import {
    findAssignedSubAccount,
    getAssignedSubAccountIds,
    requiresCurrentTermsAcceptance,
} from '@/helpers/guardianTerms.ts'

export const validateGuardianTermsGuard: NavigationGuard = async (to, from, next) => {
    const accountStore = useAccountStore()
    const subAccountStore = useSubAccountStore()
    const subAccountId = to.params.subAccountId ? String(to.params.subAccountId) : null

    if (!accountStore.accountData) {
        const { isSuccess } = await accountStore.loadAccountData()

        if (!isSuccess) {
            accountStore.logout(true)

            return next({
                name: routeNames.LOGIN_VIEW_NAME,
            })
        }
    }

    subAccountStore.setSubAccountsOptionsFromAccountData(accountStore.accountData)

    const assignedSubAccountIds = getAssignedSubAccountIds(accountStore.accountData)
    const fallbackSubAccountId = assignedSubAccountIds[0]

    if (!subAccountId) {
        const fallbackSubAccount = findAssignedSubAccount(
            accountStore.accountData,
            fallbackSubAccountId,
        )

        if (requiresCurrentTermsAcceptance(fallbackSubAccount)) {
            return next({
                name: routeNames.GUARDIAN_TERMS_VIEW_NAME,
                params: {
                    subAccountId: fallbackSubAccountId,
                },
            })
        }

        return next({
            name: fallbackSubAccountId ? routeNames.HOME_VIEW_NAME : routeNames.LOGIN_VIEW_NAME,
            params: fallbackSubAccountId ? { subAccountId: fallbackSubAccountId } : undefined,
        })
    }

    if (!assignedSubAccountIds.includes(subAccountId)) {
        return next({
            name: fallbackSubAccountId ? routeNames.HOME_VIEW_NAME : routeNames.LOGIN_VIEW_NAME,
            params: fallbackSubAccountId ? { subAccountId: fallbackSubAccountId } : undefined,
        })
    }

    const assignedSubAccount = findAssignedSubAccount(accountStore.accountData, subAccountId)

    if (!requiresCurrentTermsAcceptance(assignedSubAccount)) {
        return next({
            name: routeNames.HOME_VIEW_NAME,
            params: {
                subAccountId,
            },
        })
    }

    return next()
}
